import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Button } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useLanguage } from '../../../../context/LanguageContext';
import { Store } from '../../../../types/Store';
import { EditCoordinatesDrawer } from '../../UpdateStore/components/Drawers';
import { useStoreData } from '../../../../context/StoreDataContext';
import { formatPhoneNumber } from '../../../../utils/phoneFormatter';

interface StoreCoordinatesProps {
    store: Store;
    isEditable?: boolean;
}

const InfoRow = ({ label, value, children }: { label: string; value?: string | number | null; children?: React.ReactNode }) => (
    <tr>
        <td style={{ padding: '8px 0', verticalAlign: 'top', width: '40%' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        </td>
        <td style={{ padding: '8px 0', paddingLeft: '16px' }}>
            {children || <Typography variant="body2">{value || '-'}</Typography>}
        </td>
    </tr>
);

const StoreCoordinates = ({ store, isEditable = false }: StoreCoordinatesProps) => {
    const { t } = useLanguage();
    const { wayTypes } = useStoreData();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const getWayTypeName = (wayTypeId: string | number | null | undefined) => {
        if (!wayTypeId) return '';
        const wayType = wayTypes?.find(wt => wt.id.toString() === wayTypeId.toString());
        return wayType ? wayType.name : '';
    };

    const addressLines = [
        store.ADDRESS1,
        store.ADDRESS2,
        [
                store.ADDRESS3_NUM,
                getWayTypeName(store.ADDRESS3_TYPE),
                store.ADDRESS3_NAME,
        ].filter(Boolean).join(' '),
        store.ADDRESS4,
    ];

    return (
        <>
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LocationOnIcon sx={{ mr: 1, color: '#556ee6' }} />
                            <Typography variant="h6">{t('pages.store.addressInformation')}</Typography>
                        </Box>
                        {isEditable && (
                            <Button 
                                size="small" 
                                variant="text"
                                color="primary" 
                                onClick={() => setDrawerOpen(true)}
                            >
                                {t('common.edit')}
                            </Button>
                        )}
                    </Box>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                        <InfoRow label={t('pages.store.address')}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {addressLines.map((line, index) => (
                                    <Typography key={index} variant="body2" sx={{ wordBreak: 'break-word' }}>
                                        {line || '-'}
                                    </Typography>
                                ))}
                                <Box sx={{ mt: 0.5 }}>
                                    {(store.ZIP_CODE || store.LOCAL_CITY_NAME) && (
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {[store.ZIP_CODE, store.LOCAL_CITY_NAME].filter(Boolean).join(' ')}
                                        </Typography>
                                    )}
                                    {store.LOCAL_COUNTRY_NAME && (
                                        <Typography variant="body2" color="text.secondary">
                                            {store.LOCAL_COUNTRY_NAME}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </InfoRow>
                        <InfoRow label={t('pages.store.cityRef')} value={store.SHORT_CITY_NAME} />
                        <InfoRow label={t('pages.store.email')} value={store.EMAIL_ADDRESS} />
                        <InfoRow label={t('pages.store.landlinePhone')}>
                            <Typography variant="body2">
                                {store.DIALING_CODE ? `(${store.DIALING_CODE}) ` : ''}{formatPhoneNumber(store.PHONE_NUMBER) || '-'}
                            </Typography>
                        </InfoRow>
                    </tbody>
                </table>
            </CardContent>
        </Card>
        
        <EditCoordinatesDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            store={store}
        />
        </>
    );
};

export default StoreCoordinates;
